import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, Send, ArrowLeft, LogOut, Save, Loader2, CheckCircle2, AlertCircle,
  Eye, EyeOff, Trash2, RefreshCw, KeyRound, Shield, Building2, AtSign,
  ExternalLink, Info, Users, Plus, X, DollarSign,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const parseError = (err, fallback = 'Error') => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || x).join(' | ');
  return d || err?.message || fallback;
};

const EmailConfigPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [formProvider, setFormProvider] = useState(null);   // v1.14.1 · la ayuda lateral sigue al proveedor elegido

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    const u = JSON.parse(stored);
    if (u.username !== 'Gerencia' && u.role !== 'admin' && !(u.extra_modules || []).includes('email_config')) {
      toast.error('Acceso restringido a Gerencia');
      navigate('/login');
      return;
    }
    setUser(u);
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/email-config`, { headers: authHeaders() });
      setModules(r.data.modules || []);
      if (!activeModule && r.data.modules?.length) setActiveModule(r.data.modules[0].module_key);
    } catch (e) { toast.error(parseError(e)); }
    finally { setLoading(false); }
  }, [activeModule]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user) return null;
  const current = modules.find(m => m.module_key === activeModule);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="email-config-page">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">{/* v1.13.0 · en el celular baja «Salir» */}
          <div className="flex min-w-0 flex-wrap items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/contratos')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Contratos
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2F3D8E] flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#2F3D8E]">Configuracion de Emails</h1>
                <p className="text-sm text-gray-500">Microsoft 365 o Gmail · una cuenta por modulo</p>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { localStorage.clear(); navigate('/login'); }}>
            <LogOut className="w-4 h-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#2F3D8E]" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {/* Sidebar */}
            <div className="md:col-span-4">
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h3 className="text-sm font-bold text-gray-700">Modulos del proceso</h3>
                  <p className="text-[11px] text-gray-500">Selecciona uno para configurar su correo</p>
                </div>
                <ul>
                  {modules.map(m => (
                    <li key={m.module_key}>
                      <button
                        onClick={() => setActiveModule(m.module_key)}
                        data-testid={`module-tab-${m.module_key}`}
                        className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition-colors ${activeModule === m.module_key ? 'bg-blue-50 border-l-4 border-l-[#2F3D8E]' : ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-sm text-gray-800">{m.module_key}</div>
                            <div className="text-[11px] text-gray-500 mt-0.5">{m.label}</div>
                          </div>
                          {m.configured ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-amber-400" />
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {(formProvider || current?.config?.provider || 'graph') !== 'gmail' && <GraphHelpPanel />}
            </div>

            {/* Detail */}
            <div className="md:col-span-8">
              {current && (
                <ModuleConfigForm
                  onProvider={setFormProvider}
                  moduleData={current}
                  onSaved={load}
                  onDeleted={load}
                />
              )}
              <PaymentRecipientsCard />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ============================================================================
// Guia de obtencion de credenciales Azure AD
// ============================================================================
const GraphHelpPanel = () => (
  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 space-y-2" data-testid="graph-help-panel">
    <div className="flex items-center gap-1.5 font-bold text-[#2F3D8E]">
      <Info className="w-4 h-4" />
      <span>Como obtener las credenciales (5 minutos)</span>
    </div>
    <ol className="list-decimal list-inside space-y-1 ml-1 text-[11.5px] leading-relaxed">
      <li>
        Entra a <a href="https://portal.azure.com" target="_blank" rel="noreferrer" className="text-blue-700 underline inline-flex items-center gap-0.5">portal.azure.com <ExternalLink className="w-3 h-3" /></a> con la cuenta admin de tu tenant.
      </li>
      <li>
        Buscar <strong>"App registrations"</strong> → <strong>New registration</strong>. Nombre: <code className="bg-white px-1">Analytica Mailer</code>. Audience: <em>"Accounts in this organizational directory only"</em>. Sin Redirect URI.
      </li>
      <li>
        En el Overview copia: <strong>Application (client) ID</strong> y <strong>Directory (tenant) ID</strong>.
      </li>
      <li>
        En <strong>Certificates &amp; secrets</strong> → <strong>New client secret</strong>. Copia el <strong>Value</strong> inmediatamente (no se muestra dos veces).
      </li>
      <li>
        En <strong>API permissions</strong> → <strong>Add a permission</strong> → <strong>Microsoft Graph</strong> → <strong>Application permissions</strong> → buscar <code className="bg-white px-1">Mail.Send</code> → Add.
      </li>
      <li>
        Click <strong>Grant admin consent for &lt;tu tenant&gt;</strong> y confirmar (sin esto, fallara con 403).
      </li>
      <li>
        En <strong>Buzon remitente</strong> usa el UPN de una cuenta de usuario <strong>con licencia Exchange</strong> (no un grupo Microsoft 365). Ejemplo: <code className="bg-white px-1">no-reply@analytica-ec.com</code>.
      </li>
    </ol>
    <div className="bg-white border border-blue-200 rounded p-2 text-[11px] mt-2">
      <strong>Opcional (recomendado para seguridad):</strong> en Exchange Online PowerShell, restringe la app solo a un buzon especifico con <code>New-ApplicationAccessPolicy</code>. De lo contrario, la app podra enviar como cualquier buzon del tenant.
    </div>
  </div>
);

// ============================================================================
// v1.13.0 · Gmail (cuenta gratuita o Workspace) con contraseña de aplicación
// ============================================================================
const GmailFields = ({ form, setForm, moduleKey, configured }) => {
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (moduleKey !== 'crm' || !configured) { setStatus(null); return; }
    axios.get(`${API}/email-config/${moduleKey}/inbound-status`, { headers: authHeaders() })
      .then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [moduleKey, configured]);
  const when = (iso) => { try { return new Date(iso).toLocaleString('es-EC', { timeZone: 'America/Guayaquil' }); } catch { return iso; } };
  return (
    <div className="space-y-4" data-testid="gmail-fields">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs flex items-center gap-1"><AtSign className="w-3 h-3" /> Dirección de Gmail *</Label>
          <Input type="email" value={form.gmail_address} onChange={e => setForm({ ...form, gmail_address: e.target.value })}
                 placeholder="ventas.analytica@gmail.com" data-testid="gmail-address-input" />
        </div>
        <div>
          <Label className="text-xs">Nombre del remitente</Label>
          <Input value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })}
                 placeholder="Analytica" data-testid="from-name-input" />
        </div>
      </div>
      <div>
        <Label className="text-xs flex items-center gap-1"><KeyRound className="w-3 h-3 text-red-500" /> Contraseña de aplicación *</Label>
        <div className="relative">
          <Input type={show ? 'text' : 'password'} value={form.app_password} onChange={e => setForm({ ...form, app_password: e.target.value })}
                 placeholder={configured ? 'Dejar •••••••• para no cambiar' : 'abcd efgh ijkl mnop'} autoComplete="new-password"
                 data-testid="gmail-app-password-input" />
          <button type="button" onClick={() => setShow(v => !v)} aria-label={show ? 'Ocultar' : 'Mostrar'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[10px] text-amber-700 mt-1">Son 16 letras que Google genera para esta app. NO es la contraseña normal de Gmail.</p>
      </div>
      {moduleKey === 'crm' && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2" data-testid="gmail-inbound">
          <label className="flex items-start gap-2 text-xs cursor-pointer">
            <input type="checkbox" className="mt-0.5 w-4 h-4" checked={form.receive_inbound}
                   onChange={e => setForm({ ...form, receive_inbound: e.target.checked })} data-testid="gmail-receive-toggle" />
            <span><strong>Recibir respuestas en el CRM.</strong> Cada 1–2 minutos los correos nuevos de clientes (contactos del CRM) entran a Conversaciones. No se marcan como leídos en Gmail y no se importa lo anterior a la activación.</span>
          </label>
          <label className={`flex items-start gap-2 text-xs ${form.receive_inbound ? 'cursor-pointer' : 'opacity-50'}`}>
            <input type="checkbox" className="mt-0.5 w-4 h-4" disabled={!form.receive_inbound} checked={form.inbound_create_contacts}
                   onChange={e => setForm({ ...form, inbound_create_contacts: e.target.checked })} data-testid="gmail-create-toggle" />
            <span>Crear un contacto nuevo cuando escriba alguien que no está en el CRM (se ignoran boletines y avisos automáticos).</span>
          </label>
          {status?.enabled && (
            <p className="text-[11px] text-gray-600" data-testid="gmail-inbound-status">
              {status.last_run ? `Última revisión: ${when(status.last_run)} · ${status.total} correo(s) recibidos en total` : 'Esperando la primera revisión…'}
              {status.last_error && <span className="block text-red-600">Error: {status.last_error}</span>}
            </p>
          )}
        </div>
      )}
      <GmailHelpPanel />
    </div>
  );
};

const GmailHelpPanel = () => (
  <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-900 space-y-2" data-testid="gmail-help-panel">
    <div className="flex items-center gap-1.5 font-bold text-[#2E7A22]"><Info className="w-4 h-4" /><span>Gmail: cómo crear la contraseña de aplicación</span></div>
    <ol className="list-decimal list-inside space-y-1 ml-1 text-[11.5px] leading-relaxed">
      <li>Entre a <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">myaccount.google.com/security <ExternalLink className="w-3 h-3" /></a> con la cuenta de Gmail que enviará.</li>
      <li>Active la <strong>Verificación en 2 pasos</strong> (si ya está activa, siga).</li>
      <li>Abra <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">Contraseñas de aplicaciones <ExternalLink className="w-3 h-3" /></a>, escriba «Analytica» y pulse <strong>Crear</strong>.</li>
      <li>Copie las 16 letras y péguelas aquí. Google no las vuelve a mostrar.</li>
    </ol>
    <p className="text-[11px]">Gmail gratuito envía hasta ~500 correos al día. Si cambia la contraseña de la cuenta, Google anula la contraseña de aplicación y hay que crear otra.</p>
  </div>
);

// ============================================================================
// Module Config Form (Graph OAuth2)
// ============================================================================
const ModuleConfigForm = ({ moduleData, onSaved, onDeleted, onProvider }) => {
  const c = moduleData.config || {};
  const emptyForm = {
    provider: c.provider || 'graph',   // v1.13.0 · Microsoft 365 o Gmail
    gmail_address: c.gmail_address || '',
    app_password: c.app_password_set ? '••••••••' : '',
    receive_inbound: !!c.receive_inbound,
    inbound_create_contacts: !!c.inbound_create_contacts,
    tenant_id: c.tenant_id || '',
    client_id: c.client_id || '',
    client_secret: c.client_secret_set ? '••••••••' : '',
    sender_user_id: c.sender_user_id || '',
    from_name: c.from_name || 'Analytica',
    enabled: c.enabled !== false,
  };
  const [form, setForm] = useState(emptyForm);
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  // Reset on module switch
  useEffect(() => {
    const cc = moduleData.config || {};
    setForm({
      provider: cc.provider || 'graph',
      gmail_address: cc.gmail_address || '',
      app_password: cc.app_password_set ? '••••••••' : '',
      receive_inbound: !!cc.receive_inbound,
      inbound_create_contacts: !!cc.inbound_create_contacts,
      tenant_id: cc.tenant_id || '',
      client_id: cc.client_id || '',
      client_secret: cc.client_secret_set ? '••••••••' : '',
      sender_user_id: cc.sender_user_id || '',
      from_name: cc.from_name || 'Analytica',
      enabled: cc.enabled !== false,
    });
    setShowSecret(false);
    setTestEmail('');
  }, [moduleData.module_key, moduleData.config]); // eslint-disable-line

  const isGmail = form.provider === 'gmail';
  useEffect(() => { onProvider?.(form.provider); }, [form.provider, onProvider]);
  const save = async () => {
    if (isGmail) {   // v1.13.0
      if (!form.gmail_address || !form.app_password) { toast.error('Escriba la dirección de Gmail y la contraseña de aplicación'); return; }
      setBusy(true);
      try {
        await axios.put(`${API}/email-config/${moduleData.module_key}`, form, { headers: authHeaders() });
        toast.success(`Gmail guardado para "${moduleData.module_key}"`);
        onSaved?.();
      } catch (e) { toast.error(parseError(e)); }
      finally { setBusy(false); }
      return;
    }
    if (!form.tenant_id || !form.client_id || !form.sender_user_id) {
      toast.error('Tenant ID, Client ID y buzon remitente son obligatorios');
      return;
    }
    if (!form.client_secret) {
      toast.error('Ingrese el Client Secret (o use el placeholder •••• si ya esta guardado)');
      return;
    }
    setBusy(true);
    try {
      await axios.put(`${API}/email-config/${moduleData.module_key}`, form, { headers: authHeaders() });
      toast.success(`Configuracion guardada para "${moduleData.module_key}"`);
      onSaved?.();
    } catch (e) { toast.error(parseError(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminar la configuracion de correo del modulo "${moduleData.module_key}"?`)) return;
    try {
      await axios.delete(`${API}/email-config/${moduleData.module_key}`, { headers: authHeaders() });
      toast.success('Eliminada');
      onDeleted?.();
    } catch (e) { toast.error(parseError(e)); }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) { toast.error('Ingresa un email de prueba valido'); return; }
    setTesting(true);
    try {
      const r = await axios.post(`${API}/email-config/test`, {
        ...form,
        test_recipient: testEmail,
        module_key: moduleData.module_key,
      }, { headers: authHeaders() });
      toast.success(`Email de prueba enviado a ${r.data.sent_to}${r.data.inbox_ok ? ' · bandeja de entrada OK' : ''}`);
    } catch (e) { toast.error(parseError(e, 'Error enviando email de prueba')); }
    finally { setTesting(false); }
  };

  return (
    <div className="bg-white rounded-xl border" data-testid={`config-form-${moduleData.module_key}`}>
      <div className="px-5 py-4 border-b bg-gradient-to-r from-[#2F3D8E] to-[#5DA5D8] text-white rounded-t-xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80 flex items-center gap-1.5">
              <Shield className="w-3 h-3" /> {isGmail ? 'Gmail · contraseña de aplicación' : 'Microsoft Graph · OAuth2'}
            </div>
            <h2 className="font-bold text-xl">{moduleData.module_key}</h2>
            <p className="text-xs opacity-90 mt-0.5">{moduleData.label}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${moduleData.configured ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {moduleData.configured ? 'Configurado' : 'Sin configurar'}
            </span>
            {moduleData.configured && (
              <button onClick={remove} className="text-white/80 hover:text-red-200" title="Eliminar configuracion" data-testid="delete-config-btn">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* v1.13.0 · proveedor */}
        <div>
          <Label className="text-xs">Proveedor de correo</Label>
          <div className="mt-1 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Proveedor de correo">
            {[['graph', 'Microsoft 365 (Outlook)'], ['gmail', 'Google (Gmail)']].map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={form.provider === k} data-testid={`provider-${k}`}
                      onClick={() => setForm({ ...form, provider: k })}
                      className={`rounded-lg border px-3 py-2 text-sm font-semibold ${form.provider === k ? 'border-[#2F3D8E] bg-blue-50 text-[#2F3D8E]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {isGmail ? <GmailFields form={form} setForm={setForm} moduleKey={moduleData.module_key} configured={moduleData.configured} /> : (<>
        {/* Tenant + Client */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs flex items-center gap-1"><Building2 className="w-3 h-3" /> Tenant ID *</Label>
            <Input value={form.tenant_id} onChange={e => setForm({ ...form, tenant_id: e.target.value })}
                   placeholder="00000000-0000-0000-0000-000000000000"
                   data-testid="tenant-id-input" />
            <p className="text-[10px] text-gray-500 mt-1">Directory (tenant) ID en Azure Entra.</p>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><KeyRound className="w-3 h-3" /> Client ID *</Label>
            <Input value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}
                   placeholder="App Registration · Application (client) ID"
                   data-testid="client-id-input" />
            <p className="text-[10px] text-gray-500 mt-1">Application (client) ID del App Registration.</p>
          </div>
        </div>

        {/* Client secret */}
        <div>
          <Label className="text-xs flex items-center gap-1"><KeyRound className="w-3 h-3 text-red-500" /> Client Secret *</Label>
          <div className="relative">
            <Input type={showSecret ? 'text' : 'password'} value={form.client_secret}
                   onChange={e => setForm({ ...form, client_secret: e.target.value })}
                   placeholder={moduleData.configured ? 'Dejar •••••••• para no cambiar' : 'Valor del secret (no el Secret ID)'}
                   data-testid="client-secret-input" />
            <button type="button" onClick={() => setShowSecret(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-[10px] text-amber-700 mt-1">
            Importante: copia el <strong>Value</strong> (no el Secret ID). El secreto solo se muestra una vez al crearlo en Azure.
          </p>
        </div>

        {/* Sender mailbox */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs flex items-center gap-1"><AtSign className="w-3 h-3" /> Buzon remitente (UPN) *</Label>
            <Input type="email" value={form.sender_user_id}
                   onChange={e => setForm({ ...form, sender_user_id: e.target.value })}
                   placeholder="no-reply@analytica-ec.com"
                   data-testid="sender-user-input" />
            <p className="text-[10px] text-gray-500 mt-1">
              Usuario con licencia Exchange (no un grupo Microsoft 365).
            </p>
          </div>
          <div>
            <Label className="text-xs">Nombre del remitente</Label>
            <Input value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })}
                   placeholder="Analytica - QA" data-testid="from-name-input" />
            <p className="text-[10px] text-gray-500 mt-1">Solo se muestra si la app tiene permiso para sobreescribirlo.</p>
          </div>
        </div>
        </>)}

        {/* Toggle enabled */}
        <div className="flex items-center gap-6 flex-wrap pt-1">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })}
                   className="w-4 h-4" data-testid="enabled-toggle" />
            <span className={form.enabled ? 'text-green-700 font-semibold' : 'text-gray-500'}>
              {form.enabled ? 'Configuracion habilitada' : 'Deshabilitada (no enviara correos)'}
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="border-t pt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                   placeholder="email@destino-prueba.com" className="max-w-xs" data-testid="test-email-input" />
            <Button variant="outline" onClick={sendTest} disabled={testing}
                    className="border-[#4C8C3B] text-[#4C8C3B] hover:bg-[#4C8C3B] hover:text-white"
                    data-testid="send-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
              Enviar prueba
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onSaved?.()} disabled={busy}>
              <RefreshCw className="w-4 h-4 mr-1.5" /> Recargar
            </Button>
            <Button onClick={save} disabled={busy} className="bg-[#2F3D8E] hover:bg-[#1f2d6e] text-white"
                    data-testid="save-config-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
              Guardar configuracion
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Payment Recipients Card (global list for PayPal / Payphone / Transfer)
// ============================================================================
const PaymentRecipientsCard = () => {
  const [recipients, setRecipients] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/email-config/payment-recipients`, { headers: authHeaders() });
      setRecipients(r.data.recipients || []);
    } catch (e) { toast.error(parseError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/email-config/payment-recipients`,
        { recipients: next }, { headers: authHeaders() });
      setRecipients(r.data.recipients || []);
      toast.success('Destinatarios de pagos actualizados');
    } catch (e) { toast.error(parseError(e)); }
    finally { setBusy(false); }
  };

  const add = () => {
    const v = (input || '').trim().toLowerCase();
    if (!v || !v.includes('@')) { toast.error('Ingrese un email valido'); return; }
    if (recipients.includes(v)) { toast.error('Ya esta en la lista'); return; }
    const next = [...recipients, v];
    setInput('');
    save(next);
  };

  const remove = (email) => {
    const next = recipients.filter(r => r !== email);
    save(next);
  };

  return (
    <div className="bg-white rounded-xl border mt-5" data-testid="payment-recipients-card">
      <div className="px-5 py-4 border-b bg-gradient-to-r from-[#4C8C3B] to-[#2d5424] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <DollarSign className="w-5 h-5" />
          <div>
            <h2 className="font-bold text-lg">Destinatarios de Pagos</h2>
            <p className="text-xs opacity-90">
              Correos extra que reciben notificaciones de PayPal, Payphone y Transferencias (ademas de <code className="bg-white/20 px-1 rounded">finanzas@analytica-ec.com</code>)
            </p>
          </div>
        </div>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#4C8C3B]" /></div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                  placeholder="contador@empresa.com"
                  className="pl-9"
                  disabled={busy}
                  data-testid="payment-recipient-input"
                />
              </div>
              <Button onClick={add} disabled={busy || !input}
                      className="bg-[#4C8C3B] hover:bg-[#3d7330] text-white"
                      data-testid="add-payment-recipient-btn">
                <Plus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            </div>
            {recipients.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500 italic" data-testid="payment-recipients-empty">
                Sin destinatarios adicionales. Solo llegara a <strong>finanzas@analytica-ec.com</strong>.
              </div>
            ) : (
              <ul className="divide-y border rounded-lg overflow-hidden">
                {recipients.map(email => (
                  <li key={email} className="flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100">
                    <div className="flex items-center gap-2 text-sm">
                      <AtSign className="w-3.5 h-3.5 text-[#4C8C3B]" />
                      <span className="font-mono text-gray-800" data-testid={`payment-recipient-${email}`}>{email}</span>
                    </div>
                    <button onClick={() => remove(email)} disabled={busy}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            data-testid={`remove-payment-recipient-${email}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-gray-500 mt-3">
              Cambios se aplican inmediatamente para PayPal, Payphone y Transferencias bancarias.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailConfigPage;
